import { IComponentOptions, IPromise } from "angular";
import { IModalStackService } from "angular-ui-bootstrap";
import { StateParams, StateService } from "@uirouter/angular";
import { GtmhubController } from "@gtmhub/core";
import { IIndicator, IUIError } from "@gtmhub/error-handling";
import { ContributorTypeEnum, IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { INgRedux } from "@gtmhub/state-management";
import { TaskService } from "@gtmhub/tasks/services/task.service";
import { TaskEvents } from "@gtmhub/tasks/task.events";
import { GtmhubTaskStatus, statusOfTask } from "@gtmhub/tasks/utils/tasks-status";
import { getCurrentUserId } from "@gtmhub/users";
import { IPermissionsStoreState, hasPermissions } from "@gtmhub/users/redux";
import { Flavor, recommendation } from "@webapp/analytics/models/analytics.model";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { CfMap } from "@webapp/custom-fields/models/custom-fields.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { ParentOkrSelectorType } from "@webapp/okrs/models/parent-selector.models";
import { PlanningSessionStatus, Session } from "@webapp/sessions/models/sessions.model";
import { Task } from "@webapp/tasks/models/tasks.models";
import { ITaskFormScope } from "../task-form.component";

type TaskParentItem = (IGoal | Metric) & { type?: ParentOkrSelectorType };

interface ITaskSelectedParent {
  parent: TaskParentItem;
  type: ParentOkrSelectorType;
  id: string;
}
interface FieldsToPreventDefault {
  name: boolean;
  dueDate: boolean;
  linkedTo: boolean;
}

interface ISessionsRedux {
  allSessions: Session[];
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

const bindStateToCtrl = (state: ISessionsStoreState): ISessionsRedux => {
  return {
    allSessions: state.sessions.items,
    reduxItemsFetched: state.sessions.isFetched,
    reduxItemsError: state.sessions.error,
  };
};

class CreateTaskFormCtrl extends GtmhubController {
  private taskEvents: TaskEvents;
  private fieldsToPreventDefault: FieldsToPreventDefault;
  private allSessions: Session[];
  public indicators: {
    loading?: IIndicator;
    creating?: IIndicator;
    loadingAssignee?: IIndicator;
  };
  public task: Task;
  public parent: TaskParentItem;
  public statuses = GtmhubTaskStatus;
  public allOpenSessionsIds: string[];
  static $inject = ["$scope", "$state", "$ngRedux", "$uibModalStack", "$stateParams", "GoalService", "MetricService", "TaskService", "AnalyticsService"];

  constructor(
    private $scope: ITaskFormScope,
    private $state: StateService,
    private $ngRedux: INgRedux,
    private $uibModalStack: IModalStackService,
    private $stateParams: StateParams,
    private goalService: GoalService,
    private metricService: MetricService,
    private taskService: TaskService,
    private analyticsService: AnalyticsService
  ) {
    super();
    this.indicators = {
      loading: { progress: true },
      loadingAssignee: { progress: true },
    };
    this.taskEvents = new TaskEvents(this.$scope);
    this.fieldsToPreventDefault = {
      name: true,
      dueDate: true,
      linkedTo: true,
    };
  }

  $onInit(): void {
    this.onDestroy(this.$ngRedux.connect(bindStateToCtrl)(this));
    this.init();
  }

  private init(): void {
    delete this.indicators.loading;
    this.setNewTaskInitialDetails();

    if (this.task.parentId) {
      this.getParent(this.task.parentId, this.task.parentType as ParentOkrSelectorType).catch((error) => (this.indicators.loading = { error }));
    }

    this.getAllOpenSessionsIds();
  }

  private setNewTaskInitialDetails(): void {
    this.task = {
      name: "",
      ownerId: this.assignCurrentUserAsInitialOwner(),
      status: this.statuses.toDo,
      description: "",
      parentId: this.getParentId(),
      parentType: this.$stateParams.parentType,
    } as Task;
  }

  private assignCurrentUserAsInitialOwner(): string {
    const currentUserId = getCurrentUserId();
    delete this.indicators.loadingAssignee;

    return currentUserId;
  }

  private getParentId(): string {
    if (this.$stateParams.parentType === ContributorTypeEnum.metric) {
      return this.$stateParams.metricId;
    }

    // defaults to parent type 'goal'
    return this.$stateParams.parentId || this.$stateParams.id || "";
  }

  private getParent(parentId: string, parentType: ParentOkrSelectorType): IPromise<TaskParentItem | { error }> {
    const params = {
      fields: "name,sessionId",
    };

    const getParentPromise: IPromise<TaskParentItem> =
      parentType === ContributorTypeEnum.metric ? this.metricService.getMetric(parentId) : this.goalService.getGoal(parentId, params);

    return getParentPromise.then(
      (parent: TaskParentItem) => (this.parent = { ...parent, type: parentType }),
      (error) => (this.indicators.loading = { error })
    );
  }

  private getAllOpenSessionsIds(): void {
    this.allOpenSessionsIds = this.allSessions.reduce((acc, session) => {
      if (session.status !== PlanningSessionStatus.ARCHIVED && session.status !== PlanningSessionStatus.INPROGRESS) {
        acc.push(session.id);
      }

      return acc;
    }, []);
  }

  dismissTopModal(): void {
    this.$uibModalStack.dismiss(this.$uibModalStack.getTop().key);
  }

  keydown(event: KeyboardEvent, field: string): void {
    const isEnterKey = event.key === "Enter";

    if (!isEnterKey || !this.fieldsToPreventDefault[field]) {
      return;
    }

    event.preventDefault(); // prevents opening the Cf Drop-down menu when hitting Enter
  }

  changeOwner(ids: string[]): void {
    if (!ids || !ids.length) {
      this.task.ownerId = "";
      return;
    }

    this.task.ownerId = ids[0];
  }

  changeStatus(status: GtmhubTaskStatus): void {
    this.task.status = status;
  }

  getTaskStatus(): string {
    return statusOfTask(this.task);
  }

  selectParent(parent: TaskParentItem): void {
    if (!parent) {
      this.removeTaskParent();

      return;
    }

    const selected: ITaskSelectedParent = {
      parent,
      type: parent.type,
      id: parent.id,
    };

    this.updateTaskParent(selected);
  }

  private updateTaskParent(selected: ITaskSelectedParent): void {
    this.task.parentId = selected.id;
    this.task.parentType = selected.type;
    this.parent = selected.parent;
  }

  private removeTaskParent(): void {
    this.task.parentId = "";
    this.task.parentType = null;
    this.parent = null;
  }

  updateCustomFields(customFields: CfMap): void {
    this.task.customFields = customFields;
  }

  isFormInvalid(): boolean {
    return !this.task.name || !this.task.ownerId || !this.task.status;
  }

  create(): void {
    this.indicators.creating = { progress: true };

    this.taskService
      .create(this.task)
      .then((newTask: Task) => {
        this.trackRecommendationHelpfulness(newTask);

        this.$state.go("^.^", undefined, { location: "replace" }).then(() => {
          this.$state.go("._task.task", { taskId: newTask.id }, { location: "replace" });
          delete this.indicators.creating;
        });

        this.taskEvents.broadcastTaskCreated({ task: newTask, sessionId: this.parent ? this.parent.sessionId : null });
      })
      .catch((error) => (this.indicators.creating = { error }));
  }

  private trackRecommendationHelpfulness(task: Task): void {
    // recommendation is helpful if user creates task and does not remove himself as an owner
    this.analyticsService.track(recommendation, { is_useful: task.ownerId === getCurrentUserId(), flavor: Flavor.SMART_DEFAULT, action: "creates_task;owner" });
  }

  public isCustomFieldDisabled = (): boolean => {
    return !hasPermissions(this.$ngRedux.getState<IPermissionsStoreState>(), "ManageTasks");
  };
}

export const CreateTaskFormComponent: IComponentOptions = {
  controller: CreateTaskFormCtrl,
  template: require("./create-task-form.component.html"),
  bindings: {},
};
